import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { services } from "@/data/services";
import { RevealBlock } from "./Reveal";
import { TID } from "@/lib/testIds";
import { ArrowUpRight } from "lucide-react";

export default function ServicesGrid({ compact = false }) {
  const items = compact ? services.slice(0, 8) : services;
  return (
    <section
      data-testid={TID.services.root}
      className="bg-[#111111] text-[#F5F3EF] py-24 md:py-40"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-4 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-6">
              — Services
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <RevealBlock>
              <h2 className="font-serif text-[clamp(2.5rem,6vw,6rem)] leading-[0.9] tracking-tighter">
                Fifteen disciplines.<br />
                <span className="italic text-[#C17A43]">One standard.</span>
              </h2>
            </RevealBlock>
            <p className="mt-8 font-sans text-base md:text-lg text-[#F5F3EF]/70 max-w-[54ch]">
              Every discipline is staffed by officers cross-trained in
              de-escalation, first aid, and Nova Scotia-specific site protocols.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {items.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#111111]"
            >
              <Link
                to={`/services/${s.slug}`}
                data-testid={`${TID.services.card}-${s.slug}`}
                className="group flex flex-col justify-between h-full p-8 md:p-10 min-h-[280px] hover:bg-[#1a1a1a] transition-colors duration-300"
              >
                <div className="flex items-start justify-between">
                  <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43]">
                    {s.number}
                  </p>
                  <ArrowUpRight
                    size={22}
                    className="text-[#F5F3EF]/40 group-hover:text-[#C17A43] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-300"
                  />
                </div>
                <div className="mt-10">
                  <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight group-hover:italic transition-[font-style] duration-300">
                    {s.name}
                  </h3>
                  <p className="mt-4 font-sans text-sm text-[#F5F3EF]/60 leading-relaxed">
                    {s.tagline}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {compact && (
          <div className="mt-12 flex justify-end">
            <Link
              to="/services"
              className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#F5F3EF] border-b border-[#F5F3EF] pb-1 hover:text-[#C17A43] hover:border-[#C17A43]"
            >
              View all 15 services →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
